import { api, AUDIO_BASE } from "@/lib/api";

// Audio is enhancement ONLY — never blocks the loop. Backend ElevenLabs clip
// if ready; otherwise a browser-speech fallback. All plays are best-effort.
//
// ROBUSTNESS: every play captures a monotonically increasing `token`. stop() bumps the
// token, which invalidates ANY in-flight play (network fetch resolving after the user has
// already navigated away). This is what stops the Maidaan "talking onto the next screen".
//
// V1 mute: global `so_muted` flag in localStorage silences everything at the source. All
// play functions become no-ops when muted (still return the caption text for display).
let current = null;
let token = 0;

const MUTE_KEY = "so_muted";
export function isMuted() { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (e) { return false; } }
export function setMuted(v) {
  try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch (e) {}
  if (v) stop();
  try { window.dispatchEvent(new Event("so-mute-change")); } catch (e) {}
}

function stop() {
  token += 1;
  try {
    if (current) {
      current.pause();
      current.src = "";
      current.load && current.load();
      current = null;
    }
  } catch (e) {}
  try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
}

function speak(text, opts = {}, myToken = token) {
  try {
    if (myToken !== token) return;
    if (isMuted()) return;
    if (!window.speechSynthesis || !text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = opts.lang === "en" ? "en-IN" : "hi-IN";
    u.rate = opts.rate || 0.92;
    u.pitch = opts.pitch || 1.0;
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find((x) => x.lang && x.lang.toLowerCase().startsWith(u.lang.toLowerCase().slice(0, 2)));
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch (e) {}
}

function playUrl(url, myToken) {
  return new Promise((resolve) => {
    if (myToken !== token) return resolve(false);
    if (isMuted()) return resolve(false);
    try {
      const a = new Audio(AUDIO_BASE + url);
      current = a;
      a.onended = () => resolve(true);
      a.onerror = () => resolve(false);
      a.play().then(() => {
        if (myToken !== token) { try { a.pause(); } catch (e) {} resolve(false); }
      }).catch(() => resolve(false));
    } catch (e) { resolve(false); }
  });
}

// Auto-call after a ball. Returns {text} for captions even when muted / fallback.
export async function playCall({ over_id, ball_no, lang, fallbackText, fallbackOpts, trial }) {
  stop();
  const myToken = token;
  let capturedText = fallbackText || "";
  try {
    const r = await api.audio(over_id, ball_no, "call", lang, !!trial);
    if (myToken !== token) return { text: capturedText };
    if (r && r.text) capturedText = r.text;
    if (r && r.audio_url && !isMuted()) {
      const ok = await playUrl(r.audio_url, myToken);
      if (ok) return { text: capturedText };
    }
  } catch (e) {}
  speak(capturedText, fallbackOpts || { lang }, myToken);
  return { text: capturedText };
}

// Full commentary (rarely used in V1 — hear buttons removed).
export async function playCommentary({ over_id, ball_no, lang, fallbackText, trial }) {
  stop();
  const myToken = token;
  let capturedText = fallbackText || "";
  try {
    const r = await api.audio(over_id, ball_no, "commentary", lang, !!trial);
    if (myToken !== token) return { text: capturedText };
    if (r && r.text) capturedText = r.text;
    if (r && r.audio_url && !isMuted()) {
      const ok = await playUrl(r.audio_url, myToken);
      if (ok) return { text: capturedText };
    }
  } catch (e) {}
  speak(capturedText, { lang }, myToken);
  return { text: capturedText };
}

// Narration line (slideshow, close-line). Text is client-provided, so caption is known.
export async function playLine({ text, persona, lang, situation }) {
  stop();
  const myToken = token;
  try {
    const r = await api.audioLine(text, persona, situation);
    if (myToken !== token) return { text };
    if (r && r.audio_url && !isMuted()) {
      const ok = await playUrl(r.audio_url, myToken);
      if (ok) return { text };
    }
  } catch (e) {}
  speak(text, { lang }, myToken);
  return { text };
}

export const audioCtl = { stop };
